import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttorneyGeneralSaveStep11 } from '../../types/attorneyGeneralApi';
import type { AttorneyGeneralWizardDraft } from '../../types/attorneyGeneralCaseRequest';
import { formatDateRange, formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../wizard/criminal/CriminalReviewDocumentsTable';
import { useAttorneyGeneralLookups } from '../wizard/attorneyGeneral/useAttorneyGeneralLookups';
import { isBriberySubCategory } from '../wizard/attorneyGeneral/attorneyGeneralConstants';
import { preferredAccessLabels, requiresCourtType } from '../wizard/attorneyGeneral/attorneyGeneralHelpers';
import { apiOrLookupLabel, submitterFromApiStep } from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: AttorneyGeneralWizardDraft;
  submittedAt: string;
  step11?: AttorneyGeneralSaveStep11;
  hideUploadDocumentsStep?: boolean;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

function partyBodyTypeLabel(v: string): string {
  if (v === 'government') return 'Government Ministry/ Department/Other Unincorporated Body';
  if (v === 'incorporated') return 'Public Corporation/Statutory Board/State Owned Entity/Other Incorporated Body';
  return '—';
}

function inlineFileNames(files: { originalFileName?: string; fileName?: string }[]): string {
  const names = files.map((f) => f.originalFileName?.trim() || f.fileName?.trim()).filter(Boolean);
  return names.length ? names.join(', ') : '—';
}

export default function ViewRequestAttorneyGeneralCaseDetailsTab({
  caseRequestId,
  draft,
  submittedAt,
  step11,
  hideUploadDocumentsStep = false,
}: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useAttorneyGeneralLookups('review', draft.provinceId, draft.districtId, draft.courtTypeId);

  const rtiAccessLabel = useMemo(
    () =>
      draft.rtiPreferredMethodOfAccessNames.length
        ? draft.rtiPreferredMethodOfAccessNames.join(', ')
        : preferredAccessLabels(draft.rtiPreferredMethodOfAccessIds, lookups.preferredMethodOfAccess).join(', ') ||
          '—',
    [draft, lookups.preferredMethodOfAccess],
  );

  const showHighCourt = isBriberySubCategory(draft.subFileCategoryName);

  const submitter = useMemo(() => submitterFromApiStep(step11), [step11]);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField label="Case Source" value={draft.caseSource} span={2} />
            {showHighCourt ? (
              <ReviewField
                label="High Court Name"
                value={apiOrLookupLabel(draft.highCourtName, draft.highCourtId, lookups.highCourts)}
              />
            ) : null}
            <ReviewField
              label="Magistrate Court Name"
              value={apiOrLookupLabel(draft.magistrateCourtName, draft.magistrateCourtId, lookups.magistrateCourts)}
            />
            <ReviewField
              label="Police Station"
              value={apiOrLookupLabel(draft.policeStationName, draft.policeStationId, lookups.policeStations)}
            />
            <ReviewField
              label="Country Involved"
              value={apiOrLookupLabel(draft.countryInvolvedName, draft.countryInvolvedId, lookups.countries)}
            />
            <ReviewField label="Direction of Request" value={draft.directionOfRequest} span={2} />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Has previous advice been received?" value={yesNoLabel(draft.hasPreviousAdvice)} />
            {draft.hasPreviousAdvice === 'yes' ? (
              <ReviewField label="Previous Advice Attachment" value={inlineFileNames(draft.previousAdviceUploads)} />
            ) : null}
            <ReviewField label="Connected Advice Case?" value={yesNoLabel(draft.hasConnectedAdviceCase)} />
            {draft.hasConnectedAdviceCase === 'yes' ? (
              <ReviewField label="Connected Advice Case Details" value={draft.connectedAdviceDetails} span={2} />
            ) : null}
            <ReviewField label="Has action been instituted?" value={yesNoLabel(draft.hasActionInstituted)} />
            {requiresCourtType(draft.hasActionInstituted) ? (
              <>
                <ReviewField label="Court Type" value={draft.courtTypeName || draft.courtTypeId} />
                {draft.courtTypeOther ? <ReviewField label="Court Type (Other)" value={draft.courtTypeOther} /> : null}
              </>
            ) : null}
            {draft.hasActionInstituted === 'yes' ? (
              <ReviewField label="Court Name" value={draft.courtName || '—'} />
            ) : null}
            <ReviewField label="Is this an appeal matter?" value={yesNoLabel(draft.isAppealMatter)} />
            {draft.isAppealMatter === 'yes' ? (
              <>
                <ReviewField label="Original Court" value={draft.originalCourt} />
                <ReviewField label="Judgement/Order" value={inlineFileNames(draft.judgementOrderUploads)} />
              </>
            ) : null}
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={yesNoLabel(draft.hasConnectedMatters)} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ConnectedAgdFileRefReviewField
                  caseRequestId={caseRequestId}
                  label="Connected AGD File Reference Number"
                  fileRef={draft.connectedAgdFileRef}
                />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Party Details">
        <ReviewGrid cols={4}>
          <ReviewField label="Party Body Type" value={partyBodyTypeLabel(draft.partyBodyType)} span={2} />
          <ReviewField label="Plaintiff / Applicant Name" value={draft.plaintiffName} />
          <ReviewField label="Defendant / Respondent Name" value={draft.defendantName} />
          <ReviewField label="Other Involved Institutions" value={draft.otherInvolvedInstitutions} span={2} />
          <ReviewField label="Name of the Legal Officer/ Officer In-Charge" value={draft.legalOfficerName} />
          <ReviewField label="Contact Number" value={draft.legalOfficerContact} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Incident Details">
        <ReviewField label="Subject of the Dispute" value={draft.subjectOfDispute} fullWidth />
        <ReviewGrid cols={4}>
          <ReviewField
            label="Incident Occurred Date"
            value={formatDateRange(draft.incidentDateFrom, draft.incidentDateTo)}
          />
          <ReviewField
            label="Next Step of the Case"
            value={
              draft.nextStepOfCaseOther.trim() ||
              apiOrLookupLabel(draft.nextStepOfCaseName, draft.nextStepOfCaseId, lookups.nextSteps)
            }
          />
          <ReviewField label="Next Date of the Case" value={formatDisplayDate(draft.nextCaseDate)} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Victim Details">
        <ReviewGrid cols={4}>
          <ReviewField label="Name of the Victim" value={draft.victimName} />
          <ReviewField label="Date of Birth" value={formatDisplayDate(draft.victimDob)} />
          <ReviewField
            label="Gender"
            value={apiOrLookupLabel(draft.victimGenderName, draft.victimGenderId, lookups.genders)}
          />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 05" title="Offence & Accused Details">
        <ReviewGrid cols={4}>
          <ReviewField label="Date of Offence" value={formatDateRange(draft.offenceDateFrom, draft.offenceDateTo)} />
          <ReviewField label="Place of Offence" value={draft.placeOfOffence} span={3} />
        </ReviewGrid>
        {draft.accused.map((a, i) => (
          <ReviewSubBlock key={a.id} title={`Accused / Suspect ${i + 1}`}>
            <ReviewGrid cols={4}>
              <ReviewField label="Name" value={a.name} />
              <ReviewField
                label="Status"
                value={a.statusOther.trim() || apiOrLookupLabel('', a.status, lookups.suspectStatuses)}
              />
            </ReviewGrid>
          </ReviewSubBlock>
        ))}
      </ReviewSection>

      <ReviewSection step="Step 06" title="Accused Person Details - Extradition & MLA">
        <ReviewGrid cols={4}>
          <ReviewField label="Name of the Accused" value={draft.accusedName} />
          <ReviewField
            label="Nationality"
            value={apiOrLookupLabel(draft.nationalityName, draft.nationalityId, lookups.nationalities)}
          />
          <ReviewField
            label="Country of Origin"
            value={apiOrLookupLabel(draft.countryOfOriginName, draft.countryOfOriginId, lookups.countries)}
          />
          <ReviewField label="Date of Birth" value={formatDisplayDate(draft.accusedDob)} />
          <ReviewField
            label="Gender"
            value={apiOrLookupLabel(draft.accusedGenderName, draft.accusedGenderId, lookups.genders)}
          />
          <ReviewField label="Passport/ID Number" value={draft.passportOrIdNumber} />
          <ReviewField
            label="Current Custody Status"
            value={apiOrLookupLabel(draft.custodyStatusName, draft.custodyStatusId, lookups.custodyStatuses)}
          />
          <ReviewField label="Custodial Authority" value={draft.custodialAuthority} />
          <ReviewField label="Last Known Location" value={draft.lastKnownLocation} span={2} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 07" title="Legal & Extradition">
        <ReviewGrid cols={4}>
          <ReviewField label="Arrest Warrant Available" value={yesNoLabel(draft.arrestWarrantAvailable)} />
          {draft.arrestWarrantAvailable === 'yes' ? (
            <ReviewField label="Warrant Reference" value={draft.warrantReference} />
          ) : null}
          <ReviewField label="Applicable Treaty/Arrangement" value={draft.applicableTreatyArrangement} span={2} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 08" title="Petitioner Details">
        <ReviewGrid cols={4}>
          <ReviewField label="Petitioner Name" value={draft.petitionerName} />
          <ReviewField label="NIC" value={draft.petitionerNic} />
          <ReviewField label="Address" value={draft.petitionerAddress} span={2} />
          <ReviewField label="Contact Number" value={draft.petitionerContact} />
          <ReviewField label="Email Address" value={draft.petitionerEmail} />
          <ReviewField label="Represented by Attorney" value={yesNoLabel(draft.representedByAttorney)} />
          {draft.representedByAttorney === 'yes' ? (
            <>
              <ReviewField label="Attorney Name" value={draft.attorneyName} />
              <ReviewField label="Attorney Contact" value={draft.attorneyContact} />
            </>
          ) : null}
          <ReviewField label="Respondent Name(s)" value={draft.respondentNames} />
          <ReviewField
            label="Respondent Type"
            value={apiOrLookupLabel(draft.respondentTypeName, draft.respondentType, lookups.respondentTypes)}
          />
          <ReviewField label="Respondent Address" value={draft.respondentAddress} span={2} />
          <ReviewField label="Number of Respondents" value={draft.numberOfRespondents} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 09" title="Right to Information">
        <ReviewSubBlock title="Requester Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Name of Requestor" value={draft.rtiRequestorName} />
            <ReviewField label="NIC/ Passport Number" value={draft.rtiNicOrPassport} />
            <ReviewField label="Address" value={draft.rtiAddress} span={2} />
            <ReviewField label="Contact Number" value={draft.rtiContactNumber} />
            <ReviewField label="Email Address" value={draft.rtiEmailAddress} />
            <ReviewField label="Citizen of Sri Lanka" value={yesNoLabel(draft.rtiIsSriLankanCitizen)} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Information Request Details">
          <ReviewField label="Specific Public Authority Name" value={draft.rtiSpecificPublicAuthorityName} fullWidth />
          <ReviewField label="Requested Information Details" value={draft.rtiRequestedInformationDetails} fullWidth />
          <ReviewField label="Information Requested Time Period" value={draft.rtiInformationRequestedTimePeriod} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Additional Information">
          <ReviewField label="Preferred Method of Access" value={rtiAccessLabel} fullWidth />
          <ReviewField
            label="Preferred Language for Response"
            value={apiOrLookupLabel(
              draft.rtiPreferredLanguageName,
              draft.rtiPreferredLanguageId,
              lookups.preferredLanguages,
            )}
          />
          <ReviewField
            label="Involves Life or Personal Liberty"
            value={yesNoLabel(draft.rtiInvolvesLifeOrPersonalLiberty)}
          />
          {draft.rtiInvolvesLifeOrPersonalLiberty === 'yes' ? (
            <ReviewField label="Justification" value={draft.rtiJustification} fullWidth />
          ) : null}
          <ReviewField label="Additional Notes" value={draft.rtiAdditionalNotes} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 10" title="Timeline">
        <ReviewGrid cols={3}>
          <ReviewField
            label="Urgency Level"
            value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
          />
          <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
          <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 11" title="Submission">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      {!hideUploadDocumentsStep ? (
        <ReviewSection step="Step 12" title="Upload Documents">
          <ReviewSubBlock>
            <CriminalReviewDocumentsTable documents={draft.documents} />
          </ReviewSubBlock>
        </ReviewSection>
      ) : null}
    </div>
  );
}
